import React, { useMemo } from 'react';
import { FormControl, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { RepeatRounded } from '@mui/icons-material';
import clsx from 'clsx';
import dayjs, { Dayjs } from 'dayjs';
import { startCase } from 'lodash';
import { useTranslation } from 'react-i18next';

import './EventRecurrenceSelectDropdown.scss';
import { formatDataDate } from '../../../DataView/i18n';

interface RecurrenceOption {
  value: string | null;
  label: string;
}

interface EventRecurrenceSelectDropdownProps {
  value: string | null;
  onChange: (value: string | null) => void;
  startDate: Dayjs;
  disabled?: boolean;
  className?: string;
  iconOnly?: boolean;
}

const NONE_VALUE = '__none__';

const getOrdinalSuffix = (day: number): string => {
  if (day > 3 && day < 21) {
    return 'th';
  }

  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

const EventRecurrenceSelectDropdown: React.FC<EventRecurrenceSelectDropdownProps> = ({
  value,
  onChange,
  startDate,
  disabled = false,
  className,
  iconOnly = false,
}) => {
  const { t, i18n } = useTranslation();
  const hasRecurrence = !!value;
  const recurrenceOptions = useMemo<RecurrenceOption[]>(() => {
    const targetDate = dayjs(startDate);
    const targetDateForRecurrenceValue = targetDate.locale('en');
    const dayOfWeek = targetDateForRecurrenceValue.format('ddd').toLowerCase();
    const dayOfWeekFull = formatDataDate(i18n.language, targetDate.toDate(), {
      weekday: 'long',
    });
    const dayOfMonth = targetDateForRecurrenceValue.date();
    const dayOfMonthWithOrdinal = `${dayOfMonth}${getOrdinalSuffix(dayOfMonth)}`;
    const dayOfMonthLabel =
      i18n.language === 'en'
        ? dayOfMonthWithOrdinal
        : formatDataDate(i18n.language, targetDate.toDate(), { day: 'numeric' });
    const monthShort = targetDateForRecurrenceValue.format('MMM').toLowerCase();
    const monthName = formatDataDate(i18n.language, targetDate.toDate(), { month: 'long' });

    const defaultOptions: RecurrenceOption[] = [
      { value: null, label: t('calendar.recurrence.dontRepeat') },
      { value: 'every day', label: t('calendar.recurrence.everyDay') },
      {
        value: `every ${dayOfWeek}`,
        label: t('calendar.recurrence.everyWeekOn', { weekday: dayOfWeekFull }),
      },
      { value: 'every weekday', label: t('calendar.recurrence.everyWeekday') },
      {
        value: `every ${dayOfMonthWithOrdinal}`,
        label: t('calendar.recurrence.everyMonthOn', { day: dayOfMonthLabel }),
      },
      {
        value: `every ${dayOfMonthWithOrdinal} ${monthShort}`,
        label: t('calendar.recurrence.everyYearOn', {
          month: monthName,
          day: dayOfMonthLabel,
        }),
      },
    ];

    if (value && !defaultOptions.some((option) => option.value === value)) {
      return [
        defaultOptions[0],
        { value, label: t('calendar.recurrence.customValue', { value: startCase(value) }) },
        ...defaultOptions.slice(1),
      ];
    }

    return defaultOptions;
  }, [startDate, value, i18n.language, t]);

  const handleChange = (event: SelectChangeEvent<string>) => {
    const targetValue = event.target.value;
    onChange(targetValue === NONE_VALUE ? null : targetValue);
  };

  return (
    <FormControl
      className={clsx('event-recurrence-select', className, {
        'event-recurrence-select--icon-only': iconOnly,
        'event-recurrence-select--has-recurrence': iconOnly && hasRecurrence,
        'event-recurrence-select--no-recurrence': iconOnly && !hasRecurrence,
      })}
    >
      <Select
        disabled={disabled}
        value={value || NONE_VALUE}
        onChange={handleChange}
        variant="standard"
        IconComponent={iconOnly ? () => null : undefined}
        className="event-recurrence-select__select"
        inputProps={{ 'aria-label': t('calendar.recurrence.selectRecurrence') }}
        renderValue={
          iconOnly
            ? () => <RepeatRounded className="event-recurrence-select__trigger-icon" />
            : undefined
        }
        MenuProps={{
          PaperProps: {
            className: 'event-recurrence-select__menu-paper',
          },
        }}
      >
        {recurrenceOptions.map((option) => (
          <MenuItem
            key={option.value ?? NONE_VALUE}
            value={option.value ?? NONE_VALUE}
            className="event-recurrence-select__item"
          >
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default EventRecurrenceSelectDropdown;
